import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../utils/cn';

const chipVariants = cva(
  'inline-flex items-center gap-[6px] rounded-full font-mono text-[11px] font-medium leading-none whitespace-nowrap',
  {
    variants: {
      tone: {
        ok: 'text-ok',
        info: 'text-info',
        warn: 'text-warn',
        warn2: 'text-warn2',
        bad: 'text-bad',
        violet: 'text-violet',
        muted: 'text-fg-3',
      },
      appearance: {
        outlined: 'h-[22px] px-[9px] border',
        subtle: 'h-[22px] px-[9px] border border-transparent',
        filled: 'h-[22px] px-[9px] border border-transparent',
      },
    },
    compoundVariants: [
      { tone: 'ok', appearance: 'outlined', class: 'border-ok/35' },
      { tone: 'info', appearance: 'outlined', class: 'border-info/35' },
      { tone: 'warn', appearance: 'outlined', class: 'border-warn/35' },
      { tone: 'warn2', appearance: 'outlined', class: 'border-warn2/35' },
      { tone: 'bad', appearance: 'outlined', class: 'border-bad/40' },
      { tone: 'violet', appearance: 'outlined', class: 'border-violet/35' },
      { tone: 'muted', appearance: 'outlined', class: 'border-hairline-strong' },
      { tone: 'ok', appearance: 'subtle', class: 'bg-ok/10' },
      { tone: 'info', appearance: 'subtle', class: 'bg-info/10' },
      { tone: 'warn', appearance: 'subtle', class: 'bg-warn/10' },
      { tone: 'warn2', appearance: 'subtle', class: 'bg-warn2/10' },
      { tone: 'bad', appearance: 'subtle', class: 'bg-bad/12' },
      { tone: 'violet', appearance: 'subtle', class: 'bg-violet/10' },
      { tone: 'muted', appearance: 'subtle', class: 'bg-surface-3' },
      { tone: 'ok', appearance: 'filled', class: 'bg-ok/14' },
      { tone: 'info', appearance: 'filled', class: 'bg-info/14' },
      { tone: 'warn', appearance: 'filled', class: 'bg-warn/14' },
      { tone: 'warn2', appearance: 'filled', class: 'bg-warn2/14' },
      { tone: 'bad', appearance: 'filled', class: 'bg-bad/16' },
      { tone: 'violet', appearance: 'filled', class: 'bg-violet/14' },
      { tone: 'muted', appearance: 'filled', class: 'bg-surface-4' },
    ],
    defaultVariants: {
      tone: 'muted',
      appearance: 'outlined',
    },
  },
);

export interface ChipProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof chipVariants> {
  /**
   * When true, hide the leading colored dot (e.g. for `subtle`).
   */
  noDot?: boolean;
}

const Chip = React.forwardRef<HTMLSpanElement, ChipProps>(
  ({ className, tone, appearance = 'outlined', noDot, children, ...props }, ref) => {
    const showDot = !noDot && appearance !== 'subtle';
    return (
      <span
        ref={ref}
        className={cn(chipVariants({ tone, appearance }), className)}
        {...props}
      >
        {showDot ? (
          <span
            className="size-[6px] rounded-full bg-current"
            aria-hidden
          />
        ) : null}
        {children}
      </span>
    );
  },
);
Chip.displayName = 'Chip';

export { Chip, chipVariants };
